<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card
          v-for="(immo, index) in immoList"
          :key="immo.id"
          class="ma-4"
          elevation="8"
        >
          <v-img
            v-if="immo.info.thumb"
            :src="imageURL(immo.info.thumb.replace(/thumbnail/, 'scaled'))"
            ><template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular
                  indeterminate
                  color="grey lighten-2"
                ></v-progress-circular>
              </v-row> </template
          ></v-img>
          <div class="text-right">
            <span class="grey--text mr-2">#{{ immo.meta.ref }}</span>
          </div>
          <v-card-title primary-title>
            <div class="headline">{{ immo.info.title }}</div>
          </v-card-title>

          <v-card-text class="pa-2">
            <v-row justify="space-around">
              <v-chip v-if="immo.properties.living" outlined label>
                <v-icon left>flip_to_front</v-icon>
                {{ immo.properties.living }} m<sup>2</sup>
              </v-chip>
              <v-chip v-if="immo.properties.property" outlined label>
                <v-icon left>flip_to_back</v-icon>
                {{ immo.properties.property }} m<sup>2</sup>
              </v-chip>
              <v-chip v-if="immo.properties.bedrooms" outlined label>
                <v-icon left>single_bed</v-icon>
                {{ immo.properties.bedrooms }}
              </v-chip>
              <v-chip v-if="immo.properties.bathrooms" outlined label>
                <v-icon left>bathtub</v-icon>
                {{ immo.properties.bathrooms }}
              </v-chip>
              <v-chip v-if="immo.properties.lawn" outlined label>
                <v-icon left>subject</v-icon>
                {{ immo.properties.lawn }}
              </v-chip>
              <v-chip v-if="immo.properties.terrace" outlined label>
                <v-icon left>tab_unselected</v-icon>
                {{ immo.properties.terrace }}
              </v-chip>
            </v-row>

            <v-row justify="space-around" class="pt-2">
              <v-chip v-if="immo.properties.sea_access" outlined label>
                <v-icon left>rowing</v-icon>
                Sea Access
              </v-chip>
              <v-chip v-if="immo.properties.sea_view" outlined label>
                <v-icon left>shopping_basket</v-icon>
                Sea View
              </v-chip>
              <v-chip v-if="immo.properties.pool" outlined label>
                <v-icon left>view_day</v-icon>
                Pool
              </v-chip>
              <v-chip v-if="immo.properties.golf" outlined label>
                <v-icon left>markunread_mailbox</v-icon>
                Golf
              </v-chip>
              <v-chip v-if="immo.properties.garage" outlined label>
                <v-icon left>store</v-icon>
                Garage
              </v-chip>
              <v-chip v-if="immo.properties.parking" outlined label>
                <v-icon left>settings_cell</v-icon>
                Parking
              </v-chip>
            </v-row>
          </v-card-text>

          <v-card-text class="subtitle-1 text-end pt-0 pb-0">
            {{ immo.properties.price | formatPrice }}
          </v-card-text>

          <v-card-actions>
            <router-link :to="'/immo/' + immo.id" target="_blank">
              <v-btn icon>
                <v-icon>edit</v-icon>
              </v-btn>
            </router-link>

            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="details === index ? (details = false) : (details = index)"
            >
              <v-icon>
                {{
                  details === index
                    ? 'keyboard_arrow_up'
                    : 'keyboard_arrow_down'
                }}
              </v-icon>
            </v-btn>
            <v-btn icon @click="viewImmo = immo">
              <v-icon>aspect_ratio</v-icon>
            </v-btn>
          </v-card-actions>

          <v-card-actions>
            <v-spacer></v-spacer>
          </v-card-actions>

          <v-slide-y-transition>
            <v-card-text v-show="details === index">
              <div>
                {{ immo.info.description }}
              </div>
            </v-card-text>
          </v-slide-y-transition>
        </v-card>
      </v-col>
    </v-row>

    <div class="text-center">
      <v-pagination
        v-model="filter.page"
        :length="paginationLength"
        total-visible="5"
      ></v-pagination>
    </div>

    <v-fab-transition>
      <v-btn
        @click.stop="drawerFilter = !drawerFilter"
        fab
        small
        absolute
        top
        right
        class="mt-8"
      >
        <v-icon>filter_list</v-icon>
      </v-btn>
    </v-fab-transition>

    <v-navigation-drawer v-model="drawerFilter" app right stateless>
      <ImmoFilter :filter="filter" @filterUpdated="filterUpdated" />
    </v-navigation-drawer>
    <ImmoDialog v-if="viewImmo" :immo="viewImmo" @closed="viewImmo = null" />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ImmoFilter from '@/components/immo/Filter'
import ImmoDialog from '@/components/immo/Dialog'
import { IMMO_LIST_REQUEST } from '@/store/actions'

export default {
  name: 'List',
  components: { ImmoFilter, ImmoDialog },
  data() {
    return {
      viewImmo: null,
      details: null,
      drawerFilter: true,
      filter: {
        page: 1,
        limit: '10',
        categories: ['VILLA', 'FINCA', 'PENTHOUSE'],
        payment: 'BUY',
        status: ['AUTHORISED']
      }
    }
  },
  computed: {
    ...mapGetters(['immoList', 'listCount']),
    paginationLength() {
      return Math.ceil(this.listCount / this.filter.limit)
    }
  },
  methods: {
    ...mapActions([IMMO_LIST_REQUEST]),

    async filterUpdated(filter) {
      this.drawerFilter = false
      await this.IMMO_LIST_REQUEST(filter)
      this.scrollTo(0)
      if (this.filter.page > this.paginationLength)
        this.filter.page = this.paginationLength
    },

    imageURL(filename) {
      return `/images/${filename}`
    },
    imageThumbURL(filename) {
      return `/images/${filename.replace(/scaled/, 'thumbnail')}`
    },
    scrollTo(selector) {
      this.$vuetify.goTo(selector, {
        duration: 300,
        offset: 0,
        easing: 'easeInOutCubic'
      })
    }
  }
}
</script>

<style></style>
